/**
 * DocDraftReview — Side-by-side review of draft vs. effective version.
 *
 * Shows the current effective document alongside the proposed draft.
 * Provides approve and reject buttons for reviewers.
 *
 * CR-2026-026: For QMS documents, approval requires:
 *   - FRM-DC-004 Self-Review Checklist (21 items across 5 sections)
 *   - Change Classification (MAJOR/MINOR/PATCH + rationale)
 *   - Approval Justification (text + AI-assisted disclosure)
 * Rejection captures structured feedback with categories and severity.
 *
 * Route: /docs/:slug/review
 * API: GET /@docs/draft?slug=, GET /@docs/view, POST approve/reject
 *
 * Ref: CR-2026-025, CR-2026-026
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Grid,
  Loader,
  Icon,
  Button,
  Breadcrumb,
  Message,
  Form,
  TextArea,
  Label,
  Checkbox,
  Dropdown,
  Table,
  Divider,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, fetchApi } from '../../api';
import markdownComponents from '../markdownComponents';

/** Lazy-load react-markdown + remark-gfm */
let ReactMarkdownComponent = null;
let remarkGfmPlugin = null;
let mdLoaded = false;
let mdLoadPromise = null;

function loadMarkdownDeps() {
  if (mdLoadPromise) return mdLoadPromise;
  mdLoadPromise = Promise.all([
    import('react-markdown'),
    import('remark-gfm'),
  ]).then(([rm, rgfm]) => {
    ReactMarkdownComponent = rm.default;
    remarkGfmPlugin = rgfm.default;
    mdLoaded = true;
  });
  return mdLoadPromise;
}

const MarkdownPreview = ({ content, mdReady }) => {
  if (!content) return <p style={{ color: '#888' }}>No content</p>;
  if (ReactMarkdownComponent && mdReady) {
    return (
      <ReactMarkdownComponent remarkPlugins={[remarkGfmPlugin]} components={markdownComponents}>
        {content}
      </ReactMarkdownComponent>
    );
  }
  return (
    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>{content}</pre>
  );
};

// =========================================================================
// FRM-DC-004 Self-Review Checklist Template (v1.0)
// =========================================================================
const CHECKLIST_TEMPLATE = {
  A: {
    title: 'A. Document Identification',
    items: [
      { id: 'A1', text: 'Document ID is correct and follows naming convention' },
      { id: 'A2', text: 'Title accurately describes the document content' },
      { id: 'A3', text: 'Version number is correct and follows versioning policy' },
      { id: 'A4', text: 'Document type and domain are correctly assigned' },
      { id: 'A5', text: 'Author, owner, and reviewer fields are populated' },
    ],
  },
  B: {
    title: 'B. Content Quality',
    items: [
      { id: 'B1', text: 'Content is technically accurate and complete' },
      { id: 'B2', text: 'Language is clear, unambiguous, and appropriate' },
      { id: 'B3', text: 'All referenced documents and standards are correct' },
      { id: 'B4', text: 'Formatting is consistent throughout the document' },
      { id: 'B5', text: 'No placeholder or draft-only content remains' },
    ],
  },
  C: {
    title: 'C. Regulatory Compliance',
    items: [
      { id: 'C1', text: 'Meets applicable regulatory requirements (EU MDR, ISO 13485)' },
      { id: 'C2', text: 'Traceability to source requirements is maintained' },
      { id: 'C3', text: 'Risk assessment references are current' },
      { id: 'C4', text: 'Retention and archival requirements are specified' },
    ],
  },
  D: {
    title: 'D. Process Compliance',
    items: [
      { id: 'D1', text: 'Change was initiated with proper justification' },
      { id: 'D2', text: 'COI declarations have been recorded' },
      { id: 'D3', text: 'All affected processes and documents are identified' },
      { id: 'D4', text: 'Training requirements from this change are identified' },
    ],
  },
  E: {
    title: 'E. Integration',
    items: [
      { id: 'E1', text: 'No conflicts with other QMS documents' },
      { id: 'E2', text: 'Cross-references to other documents are valid' },
      { id: 'E3', text: 'Effective date and review schedule are appropriate' },
    ],
  },
};

const RESULT_OPTIONS = [
  { key: 'pass', value: 'pass', text: 'Pass' },
  { key: 'fail', value: 'fail', text: 'Fail' },
  { key: 'na', value: 'na', text: 'N/A' },
];

const CLASSIFICATION_OPTIONS = [
  { key: 'MAJOR', value: 'MAJOR', text: 'MAJOR — new version' },
  { key: 'MINOR', value: 'MINOR', text: 'MINOR — additive change' },
  { key: 'PATCH', value: 'PATCH', text: 'PATCH — editorial/correction' },
];

const REJECTION_CATEGORIES = [
  { key: 'technical', label: 'Technical accuracy' },
  { key: 'completeness', label: 'Completeness' },
  { key: 'regulatory', label: 'Regulatory compliance' },
  { key: 'editorial', label: 'Editorial quality' },
  { key: 'consistency', label: 'Consistency with QMS' },
];

const SEVERITY_OPTIONS = [
  { key: 'critical', value: 'critical', text: 'Critical — must fix before resubmit' },
  { key: 'major', value: 'major', text: 'Major — significant rework needed' },
  { key: 'minor', value: 'minor', text: 'Minor — small corrections needed' },
];

export default function DocDraftReview() {
  const params = useParams();
  const history = useHistory();
  const slug = params.slug || params[0] || '';
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [mdReady, setMdReady] = useState(mdLoaded);
  const [activeTab, setActiveTab] = useState('side-by-side');

  // Approval form state
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [checklistResults, setChecklistResults] = useState({});
  const [checklistNotes, setChecklistNotes] = useState({});
  const [changeClassification, setChangeClassification] = useState('PATCH');
  const [classificationRationale, setClassificationRationale] = useState('');
  const [approvalJustification, setApprovalJustification] = useState('');
  const [aiAssisted, setAiAssisted] = useState(false);
  const [aiDisclosure, setAiDisclosure] = useState('');

  // Rejection form state
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectCategories, setRejectCategories] = useState([]);
  const [rejectSeverity, setRejectSeverity] = useState('major');
  const [rejectActionRequired, setRejectActionRequired] = useState('');

  const isQms = !!doc?.qms_document_id;

  useEffect(() => {
    if (!mdLoaded) {
      loadMarkdownDeps().then(() => setMdReady(true));
    }
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    Promise.all([
      fetchApi(
        `${getApiPath()}/@docs/view?slug=${encodeURIComponent(slug)}`,
      ),
      fetch(
        `${getApiPath()}/@docs/draft?slug=${encodeURIComponent(slug)}`,
        {
          headers: getAuthHeaders(),
        },
      ).then((res) => {
        if (!res.ok) throw new Error('No draft found');
        return res.json();
      }),
    ])
      .then(([docJson, draftJson]) => {
        setDoc(docJson);
        setDraft(draftJson);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  // Check if all checklist items have a result
  const allChecklistAnswered = Object.values(CHECKLIST_TEMPLATE).every(
    (section) =>
      section.items.every(
        (item) =>
          checklistResults[item.id] &&
          ['pass', 'fail', 'na'].includes(checklistResults[item.id]),
      ),
  );

  const hasFailingChecklist = Object.values(checklistResults).some(
    (r) => r === 'fail',
  );

  const canApprove =
    !isQms ||
    (allChecklistAnswered &&
      !hasFailingChecklist &&
      approvalJustification.trim().length > 0);

  const handleApprove = async () => {
    setProcessing(true);
    setError(null);

    try {
      const payload = {
        slug,
        comment: approvalJustification || 'Approved via review UI',
      };

      // Include evidence for QMS documents
      if (isQms) {
        // Build checklist evidence
        const sections = {};
        for (const [sectionKey, section] of Object.entries(
          CHECKLIST_TEMPLATE,
        )) {
          sections[sectionKey] = {
            title: section.title,
            items: section.items.map((item) => ({
              id: item.id,
              text: item.text,
              result: checklistResults[item.id] || 'na',
              notes: checklistNotes[item.id] || '',
            })),
          };
        }

        payload.checklist = {
          frm_dc_004_version: '1.0',
          sections,
          outcome: {
            decision: hasFailingChecklist ? 'fail' : 'pass',
            reviewer_signature: 'submitted via review UI',
          },
        };

        payload.approval_justification = {
          justification: approvalJustification,
          ai_assisted: aiAssisted,
          ai_disclosure: aiAssisted ? aiDisclosure : '',
        };

        payload.change_classification = {
          classification: changeClassification,
          rationale: classificationRationale,
        };
      }

      const res = await fetch(`${getApiPath()}/@docs/draft/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      const result = await res.json();
      setError(null);
      setSuccess(`Version ${result.version} published successfully.`);
      setDraft(null);
      setProcessing(false);

      // Redirect to document after short delay
      setTimeout(() => history.push(`/docs/${slug}`), 2000);
    } catch (err) {
      setSuccess(null);
      setError(err.message);
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectNotes.trim()) {
      setError('Reviewer notes are required when rejecting.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const payload = {
        slug,
        notes: rejectNotes,
      };

      // Include structured rejection for QMS documents
      if (isQms) {
        payload.rejection = {
          categories: rejectCategories,
          severity: rejectSeverity,
          action_required: rejectActionRequired,
        };
      }

      const res = await fetch(`${getApiPath()}/@docs/draft/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      setError(null);
      setSuccess('Draft rejected and returned to author.');
      setDraft((prev) => ({ ...prev, status: 'rejected' }));
      setShowRejectForm(false);
      setProcessing(false);
    } catch (err) {
      setSuccess(null);
      setError(err.message);
      setProcessing(false);
    }
  };

  if (loading || !mdReady) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Loader active inline="centered" content="Loading review..." />
      </Container>
    );
  }

  if (error && !doc) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
        <Link to={`/docs/${slug}`}>&larr; Back to document</Link>
      </Container>
    );
  }

  if (!draft) {
    return (
      <Container style={{ paddingTop: '2em', maxWidth: '900px' }}>
        {success ? (
          <Message positive>
            <Message.Header>Published</Message.Header>
            <p>{success}</p>
            <p>
              <Link to={`/docs/${slug}`}>View document &rarr;</Link>
            </p>
          </Message>
        ) : (
          <Message info>
            <Message.Header>No Draft to Review</Message.Header>
            <p>There is no active draft for this document.</p>
            <p>
              <Link to={`/docs/${slug}`}>&larr; Back to document</Link>
            </p>
          </Message>
        )}
      </Container>
    );
  }

  return (
    <Container
      style={{ paddingTop: '2em', paddingBottom: '2em', maxWidth: '1200px' }}
    >
      <Helmet title={`Review Draft — ${doc?.title || slug}`} />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/docs">
          Knowledge Base
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section as={Link} to={`/docs/${slug}`}>
          {doc?.title || slug}
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>Review Draft</Breadcrumb.Section>
      </Breadcrumb>

      <Header as="h1">
        <Icon name="eye" />
        <Header.Content>
          Draft Review
          <Header.Subheader>
            {doc?.title} — v{doc?.version} → v{draft.version}
          </Header.Subheader>
        </Header.Content>
      </Header>

      {/* Draft status */}
      <Message
        color={draft.status === 'in_review' ? 'orange' : 'blue'}
        icon={draft.status === 'in_review' ? 'clock' : 'edit'}
        header={`Draft Status: ${draft.status === 'in_review' ? 'In Review' : 'Draft'}`}
        content={`Created by ${draft.created_by}. Last updated: ${new Date(draft.updated_at).toLocaleString()}`}
      />

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}
      {success && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>{success}</p>
        </Message>
      )}

      {/* View toggle */}
      <Button.Group size="small" style={{ marginBottom: '1em' }}>
        <Button
          active={activeTab === 'side-by-side'}
          onClick={() => setActiveTab('side-by-side')}
        >
          Side by Side
        </Button>
        <Button
          active={activeTab === 'draft-only'}
          onClick={() => setActiveTab('draft-only')}
        >
          Draft Only
        </Button>
        <Button
          active={activeTab === 'effective-only'}
          onClick={() => setActiveTab('effective-only')}
        >
          Effective Only
        </Button>
      </Button.Group>

      {/* Content comparison */}
      {activeTab === 'side-by-side' && (
        <Grid columns={2} stackable divided>
          <Grid.Column>
            <Segment>
              <Label attached="top" color="green">
                Effective — v{doc?.version || '—'}
              </Label>
              <div
                style={{
                  fontSize: '0.95em',
                  lineHeight: '1.5',
                  marginTop: '1em',
                }}
              >
                <MarkdownPreview content={doc?.body_md} mdReady={mdReady} />
              </div>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment>
              <Label attached="top" color="blue">
                Draft — v{draft.version}
              </Label>
              <div
                style={{
                  fontSize: '0.95em',
                  lineHeight: '1.5',
                  marginTop: '1em',
                }}
              >
                <MarkdownPreview content={draft.body_md} mdReady={mdReady} />
              </div>
            </Segment>
          </Grid.Column>
        </Grid>
      )}
      {activeTab === 'draft-only' && (
        <Segment>
          <Label attached="top" color="blue">
            Draft — v{draft.version}
          </Label>
          <div
            style={{
              fontSize: '1.05em',
              lineHeight: '1.6',
              marginTop: '1em',
            }}
          >
            <MarkdownPreview content={draft.body_md} mdReady={mdReady} />
          </div>
        </Segment>
      )}
      {activeTab === 'effective-only' && (
        <Segment>
          <Label attached="top" color="green">
            Effective — v{doc?.version || '—'}
          </Label>
          <div
            style={{
              fontSize: '1.05em',
              lineHeight: '1.6',
              marginTop: '1em',
            }}
          >
            <MarkdownPreview content={doc?.body_md} mdReady={mdReady} />
          </div>
        </Segment>
      )}

      {/* Title comparison if different */}
      {draft.title && doc?.title && draft.title !== doc.title && (
        <Message info size="small" style={{ marginTop: '1em' }}>
          <Message.Header>Title Change</Message.Header>
          <p>
            <strong>Current:</strong> {doc.title}
            <br />
            <strong>Proposed:</strong> {draft.title}
          </p>
        </Message>
      )}

      {/* Action buttons */}
      {draft.status === 'in_review' && (
        <Segment style={{ marginTop: '1.5em' }}>
          <Header as="h3">Review Decision</Header>

          {showRejectForm ? (
            /* ============= REJECTION FORM ============= */
            <Form>
              <Form.Field>
                <label>Rejection Notes (required)</label>
                <TextArea
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="Explain why this draft is being rejected and what needs to change..."
                  rows={4}
                />
              </Form.Field>

              {/* CR-2026-026: Structured rejection for QMS docs */}
              {isQms && (
                <>
                  <Form.Field>
                    <label>Feedback Categories</label>
                    {REJECTION_CATEGORIES.map((cat) => (
                      <Checkbox
                        key={cat.key}
                        label={cat.label}
                        checked={rejectCategories.includes(cat.key)}
                        onChange={(e, { checked }) => {
                          setRejectCategories((prev) =>
                            checked
                              ? [...prev, cat.key]
                              : prev.filter((c) => c !== cat.key),
                          );
                        }}
                        style={{ display: 'block', marginBottom: '0.5em' }}
                      />
                    ))}
                  </Form.Field>
                  <Form.Field>
                    <label>Severity</label>
                    <Dropdown
                      selection
                      options={SEVERITY_OPTIONS}
                      value={rejectSeverity}
                      onChange={(e, { value }) => setRejectSeverity(value)}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Action Required</label>
                    <TextArea
                      value={rejectActionRequired}
                      onChange={(e) =>
                        setRejectActionRequired(e.target.value)
                      }
                      placeholder="Specific actions the author must take before resubmitting..."
                      rows={2}
                    />
                  </Form.Field>
                </>
              )}

              <Button color="red" loading={processing} onClick={handleReject}>
                <Icon name="times" /> Confirm Rejection
              </Button>
              <Button onClick={() => setShowRejectForm(false)}>Cancel</Button>
            </Form>
          ) : showApprovalForm ? (
            /* ============= APPROVAL FORM ============= */
            <Form>
              {/* FRM-DC-004 Checklist (QMS only) */}
              {isQms && (
                <>
                  <Header as="h4">
                    <Icon name="tasks" /> FRM-DC-004 Self-Review Checklist
                  </Header>
                  {Object.entries(CHECKLIST_TEMPLATE).map(
                    ([sectionKey, section]) => (
                      <Segment key={sectionKey} style={{ marginBottom: '1em' }}>
                        <Header as="h5">{section.title}</Header>
                        <Table compact size="small">
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell width={1}>ID</Table.HeaderCell>
                              <Table.HeaderCell width={8}>
                                Check Item
                              </Table.HeaderCell>
                              <Table.HeaderCell width={3}>
                                Result
                              </Table.HeaderCell>
                              <Table.HeaderCell width={4}>
                                Notes
                              </Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {section.items.map((item) => (
                              <Table.Row
                                key={item.id}
                                negative={
                                  checklistResults[item.id] === 'fail'
                                }
                                positive={
                                  checklistResults[item.id] === 'pass'
                                }
                              >
                                <Table.Cell>
                                  <strong>{item.id}</strong>
                                </Table.Cell>
                                <Table.Cell>{item.text}</Table.Cell>
                                <Table.Cell>
                                  <Dropdown
                                    selection
                                    compact
                                    options={RESULT_OPTIONS}
                                    value={checklistResults[item.id] || ''}
                                    placeholder="..."
                                    onChange={(e, { value }) =>
                                      setChecklistResults((prev) => ({
                                        ...prev,
                                        [item.id]: value,
                                      }))
                                    }
                                    style={{ minWidth: '80px' }}
                                  />
                                </Table.Cell>
                                <Table.Cell>
                                  <input
                                    type="text"
                                    value={checklistNotes[item.id] || ''}
                                    onChange={(e) =>
                                      setChecklistNotes((prev) => ({
                                        ...prev,
                                        [item.id]: e.target.value,
                                      }))
                                    }
                                    placeholder="Optional notes"
                                    style={{
                                      width: '100%',
                                      border: '1px solid #ddd',
                                      padding: '4px 6px',
                                      borderRadius: '3px',
                                    }}
                                  />
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </Segment>
                    ),
                  )}

                  {hasFailingChecklist && (
                    <Message negative>
                      <Icon name="exclamation triangle" />
                      One or more checklist items have failed. You cannot
                      approve until all items pass or are marked N/A.
                    </Message>
                  )}

                  {!allChecklistAnswered && (
                    <Message warning>
                      <Icon name="exclamation triangle" />
                      All checklist items must be answered before approval.
                    </Message>
                  )}

                  <Divider />

                  {/* Change Classification */}
                  <Header as="h4">
                    <Icon name="tag" /> Change Classification
                  </Header>
                  <Form.Field>
                    <label>Classification</label>
                    <Dropdown
                      selection
                      options={CLASSIFICATION_OPTIONS}
                      value={changeClassification}
                      onChange={(e, { value }) =>
                        setChangeClassification(value)
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Rationale</label>
                    <TextArea
                      value={classificationRationale}
                      onChange={(e) =>
                        setClassificationRationale(e.target.value)
                      }
                      placeholder="Why this classification level?"
                      rows={2}
                    />
                  </Form.Field>

                  <Divider />
                </>
              )}

              {/* Approval Justification (always shown) */}
              <Header as="h4">
                <Icon name="check circle" /> Approval Justification
              </Header>
              <Form.Field required>
                <label>Justification</label>
                <TextArea
                  value={approvalJustification}
                  onChange={(e) =>
                    setApprovalJustification(e.target.value)
                  }
                  placeholder="Why is this document being approved? Summarize your review findings..."
                  rows={3}
                />
              </Form.Field>
              {isQms && (
                <>
                  <Form.Field>
                    <Checkbox
                      label="AI tools were used in preparing or reviewing this document"
                      checked={aiAssisted}
                      onChange={(e, { checked }) => setAiAssisted(checked)}
                    />
                  </Form.Field>
                  {aiAssisted && (
                    <Form.Field>
                      <label>AI Disclosure</label>
                      <TextArea
                        value={aiDisclosure}
                        onChange={(e) => setAiDisclosure(e.target.value)}
                        placeholder="Describe how AI tools were used..."
                        rows={2}
                      />
                    </Form.Field>
                  )}
                </>
              )}

              <Divider />

              <Button
                color="green"
                loading={processing}
                disabled={!canApprove}
                onClick={handleApprove}
              >
                <Icon name="check" /> Confirm Approval
              </Button>
              <Button onClick={() => setShowApprovalForm(false)}>
                Cancel
              </Button>
            </Form>
          ) : (
            /* ============= ACTION BUTTONS ============= */
            <Button.Group>
              <Button
                color="green"
                loading={processing}
                onClick={() => {
                  if (isQms) {
                    setShowApprovalForm(true);
                  } else {
                    // Non-QMS: direct approve
                    handleApprove();
                  }
                }}
              >
                <Icon name="check" /> Approve & Publish
              </Button>
              <Button
                color="red"
                loading={processing}
                onClick={() => setShowRejectForm(true)}
              >
                <Icon name="times" /> Reject
              </Button>
            </Button.Group>
          )}
        </Segment>
      )}
    </Container>
  );
}
